import React, {
  ChangeEvent,
  Dispatch,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
  SetStateAction,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { ButtonSize, ButtonColor, Table, TableRow, Checkbox } from '@code/innovance-react-ui-kit';

import {
  Container,
  CheckboxRow,
  Separator,
  Title,
  Subtitle,
  TableWrapper,
  WarningText,
} from 'src/front/components/FormStyles';
import CustomerSearchForm, {
  SearchFormData,
  customerSearchDefaultValues,
} from 'src/front/components//CustomerSearchForm';
import AsyncButton from 'src/front/components//AsyncButton';

import {
  initialValues,
  useClientSearchDispatch,
  useProblemTrackingSystemState,
} from 'src/front/contexts/ProblemTrackingSystemContext';
import { Customer, ProblemTrackingSystemApi } from 'src/front/api/apiInstance';

import { splitCustomerName } from 'src/front/common/utils/formUtils';
import { formatDate, convertStringToDate } from 'src/front/common/utils/dateTimeUtils';

const clientTableHeaders = [
  'general_customerName_label',
  'general_patronymic_label',
  'general_customerTable_birth_title',
  'general_gender_label',
  'general_customerTable_document_title',
];

export interface ClientTableRow extends TableRow {
  customerNumber?: string;
}

interface Props {
  goToNextStep: () => void;
  unregisteredPerson: boolean;
  setUnregisteredPerson: Dispatch<SetStateAction<boolean>>;
}

const ClientSearch: FC<Props> = ({ goToNextStep, unregisteredPerson, setUnregisteredPerson }) => {
  const dispatch = useClientSearchDispatch();
  const { customerSearchFormValues, customerSearchValues } = useProblemTrackingSystemState();

  const { t } = useTranslation();
  const { unregister, register, watch, setValue } = useForm();

  const warningTextRef = useRef<HTMLDivElement>(null);

  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(
    customerSearchFormValues || customerSearchDefaultValues
  );
  const [clientList, setClientList] = useState<ClientTableRow[]>();
  const [selectedClient, setSelectedClient] = useState<ClientTableRow | undefined>(
    customerSearchValues.selectedCustomer
  );
  const [clientPage, setClientPage] = useState<number>(
    customerSearchValues.customerPageNumber || 1
  );
  const [hasClientNextPage, setHasClientNextPage] = useState<boolean>(false);
  const [clientsLoading, setClientsLoading] = useState<boolean>(false);
  const [clientWarning, setClientWarning] = useState<string>();

  useEffect(() => {
    register('unregisteredPerson');

    return () => unregister('unregisteredPerson');
  }, [register, unregister]);

  const loadClientList = useCallback(
    async (
      pageNumber = clientPage,
      forceRefresh = false,
      formValues: SearchFormData = searchFormValues
    ): Promise<void> => {
      try {
        setClientsLoading(true);
        const { birthDate, ...rest } = formValues;
        const { data } = await Customer.getCustomers({
          ...rest,
          birthDate: formatDate(birthDate),
          rowCountPerPage: 5,
          pageNumber: pageNumber || clientPage,
          refresh: !pageNumber || forceRefresh,
        });

        const customers = data.customerList.map((customer) => ({
          id: customer.customerId || '',
          columns: [
            `${customer.customerName} ${customer.customerSurname}`,
            customer.customerSecondName || '',
            `${customer.birthDate || ''} - ${customer.birthPlace || ''}`,
            customer.gender || '',
            `${customer.identityDocumentSerialCode} - ${customer.identityDocumentSerialNumber}`,
          ],
          customerNumber: customer.customerNumber,
        }));
        setClientList(customers);
        setHasClientNextPage(data.hasNextPage);
        setClientPage(pageNumber || 1);
        setClientsLoading(false);
        setClientWarning(data.warning);
      } catch (error) {
        // TODO (mert.ohancan): show error on modal or handle with error boundary
      }
    },
    [clientPage, searchFormValues]
  );

  useEffect(() => {
    const loadPreviousValues = (): void => {
      loadClientList();
    };

    if (!clientList && customerSearchValues.selectedCustomer.columns.length && !clientsLoading) {
      loadPreviousValues();
    }
  }, [clientList, customerSearchValues, clientsLoading, loadClientList]);

  const resetSelectedClient = (): void => {
    setSelectedClient(undefined);
  };

  const clearPreviousValues = (): void => {
    setClientList([]);
    resetSelectedClient();
    setValue('unregisteredPerson', false);
    setUnregisteredPerson(false);
    setClientWarning('');
  };

  const onSubmit = async (formData: SearchFormData): Promise<void> => {
    setSearchFormValues(formData);
    clearPreviousValues();
    await loadClientList(1, true, formData);
  };

  const handleSelectClient = (client: TableRow | undefined): void => {
    setValue('unregisteredPerson', false);
    setUnregisteredPerson(false);

    setSelectedClient(client);
  };

  const handleUnregisteredPerson = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    resetSelectedClient();
    setValue('unregisteredPerson', target.checked);

    setUnregisteredPerson(target.checked);
  };

  const handleContinue = async (): Promise<void> => {
    if (selectedClient || unregisteredPerson) {
      const [customerName, customerSecondName, customerBirthInfo] = selectedClient?.columns || [];
      dispatch({
        customerSearchFormValues: searchFormValues,
        customerSearchValues: selectedClient
          ? {
              customerId: selectedClient.id,
              customerNumber: selectedClient.customerNumber || '',
              clientName: splitCustomerName(customerName, customerSecondName),
              customerName: customerName.split(' ')[0],
              customerSecondName,
              customerSurname: customerName.split(' ')[1],
              birthDate: convertStringToDate(customerBirthInfo.split(' ')[0]),
              selectedCustomer: selectedClient,
              customerPageNumber: clientPage,
            }
          : {
              ...initialValues.customerSearchValues,
              customerName: searchFormValues.customerName,
              customerSecondName: searchFormValues.customerSecondName || '',
              customerSurname: searchFormValues.customerSurname,
              birthDate: searchFormValues.birthDate,
              customerPageNumber: clientPage,
            },
      });

      const { data } = await ProblemTrackingSystemApi.checkCustomer({
        customerId: selectedClient?.id || '',
      });

      if (data.result === 'OK') goToNextStep();
    } else {
      setClientWarning(t('problemTrackingSystem_noSelection_errorMessage').toString());
      setTimeout(() => warningTextRef?.current?.scrollIntoView(), 1);
    }
  };

  return (
    <Container>
      <Title>
        <Trans i18nKey="problemTrackingSystem_title" />
      </Title>
      <Separator />
      <CustomerSearchForm formValues={searchFormValues} onSubmit={onSubmit} />
      {clientList ? (
        <>
          <Separator />
          <Subtitle>
            <Trans i18nKey="cardReissue_customerInfo_text" />
          </Subtitle>
          <TableWrapper>
            <Table
              emptyTableText={t('problemTrackingSystem_notFound_errorMessage')}
              hasNextPage={hasClientNextPage}
              headers={clientTableHeaders.map((head) => t(head))}
              items={clientList}
              loading={clientsLoading}
              loadPageItems={loadClientList}
              onSelect={handleSelectClient}
              pageNumber={clientPage}
              selectedItem={selectedClient}
              clearItemOnPageChange
              withRadio
            />
          </TableWrapper>
          <CheckboxRow>
            <Checkbox
              id="unregisteredPerson"
              label={t('problemTrackingSystem_unregisteredPersonSelection_text')}
              onChange={handleUnregisteredPerson}
              value={watch('unregisteredPerson')}
            />
          </CheckboxRow>
          <AsyncButton
            color={ButtonColor.Primary}
            onClick={handleContinue}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
          />
          {clientWarning ? (
            <WarningText id="warning-text" ref={warningTextRef}>
              {clientWarning}
            </WarningText>
          ) : null}
        </>
      ) : null}
    </Container>
  );
};

export default ClientSearch;
